import React, { FC, useEffect, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { TriangleDownIcon, UserAddIcon } from '@modernloop/shared/icons';
import { Button, IconButton, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  MemberSelect_UserInfoFragment,
  SourceType,
  SubscribersSelect_ThisEmployeeFragment,
  useCurrentEmployeeDetailsQuery,
  useSubscriberAddMutation,
  useSubscriberRemoveMutation,
  useSubscribersListQuery,
} from 'src/generated/mloop-graphql';

import { createSxProps } from 'src/components/types';

import EmployeeFacePile from 'src/entities/common/EmployeeFacePile';

import { useUserInfo } from 'src/hooks/useEmployeeId';

import { PAGE_SIZE } from 'src/constants';

import MultiMemberSelect from '../Select/MemberSelect/MultiMemberSelect';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CurrentEmployeeDetailsGQL = gql`
  fragment SubscribersSelect_thisEmployee on Employee {
    id
    fullName
    internalProfilePictureUrl
  }
  query CurrentEmployeeDetails {
    thisEmployee {
      id
      ...SubscribersSelect_thisEmployee
    }
  }
`;

const SubscribersSelectEmployee = gql`
  ${EmployeeFacePile.fragments.employees}
  fragment SubscribersSelect_employee on Employee {
    id
    ...EmployeeFacePile_employee
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SubscribersListGQL = gql`
  ${SubscribersSelectEmployee}

  query SubscribersList($input: SubscribersInput!) {
    subscribers(input: $input) {
      items {
        id

        ...SubscribersSelect_employee
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SubscriberAddMutation = gql`
  ${SubscribersSelectEmployee}

  mutation SubscriberAdd($input: SubscriberAddInput!) {
    subscriberAdd(input: $input) {
      result
      subscribers {
        id

        ...SubscribersSelect_employee
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SubscriberRemoveMutation = gql`
  ${SubscribersSelectEmployee}

  mutation SubscriberRemove($input: SubscriberRemoveInput!) {
    subscriberRemove(input: $input) {
      result
      subscribers {
        id

        ...SubscribersSelect_employee
      }
    }
  }
`;

const useSxProps = () => {
  const sxProps = useMemo(
    () => ({
      root: {
        marginRight: '-8px',
      },
      employeeFacePile: {
        marginRight: '-4px',
      },
    }),
    []
  );

  return createSxProps(sxProps);
};

interface SubscribersSelectProps {
  sourceId: string;
  sourceType: SourceType;
  showSubscribeButton?: boolean;
}

const EMPTY_MEMBER_SELECT_USER_INFO_FRAGMENT_OBJECT: MemberSelect_UserInfoFragment = {
  userId: null,
  employee: {
    id: null,
    internalProfilePictureUrl: null,
    fullName: '',
  },
};

const SubscribersSelect: FC<SubscribersSelectProps> = ({ sourceId, sourceType, showSubscribeButton = false }) => {
  const { t } = useTranslation();
  const sxProps = useSxProps();
  const [selectedSubscribers, setSelectedSubscribers] = useState<MemberSelect_UserInfoFragment[]>([]);

  const {
    data: currentEmployeeData,
    loading: loadingCurrentEmployeeData,
    error: currentEmployeeDataError,
  } = useCurrentEmployeeDetailsQuery();

  const currentUserInfo = useUserInfo();
  const currentEmployeeDetails =
    currentEmployeeData && !loadingCurrentEmployeeData && !currentEmployeeDataError
      ? currentEmployeeData?.thisEmployee
      : ({} as SubscribersSelect_ThisEmployeeFragment);

  const currentEmployeeId = currentEmployeeDetails?.id ?? '';

  const { enqueueSnackbar } = useSnackbar();

  const {
    data: subscribersListData,
    error,
    loading,
  } = useSubscribersListQuery({
    variables: {
      input: {
        pageInput: {
          limit: PAGE_SIZE,
        },
        sourceId,
        sourceType,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect manages an external subscription/listener after render and cleanup.
  useEffect(() => {
    if (
      subscribersListData?.subscribers?.items &&
      subscribersListData?.subscribers?.items.length > 0 &&
      !loading &&
      !error
    ) {
      setSelectedSubscribers(
        subscribersListData?.subscribers?.items.map((subscriber) => ({
          userId: subscriber.id,
          userName: subscriber.fullName || '',
          employee: {
            id: subscriber.id,
            internalProfilePictureUrl: subscriber.internalProfilePictureUrl,
          },
        }))
      );
    }
  }, [error, loading, subscribersListData?.subscribers?.items]);

  const [addSubscribers] = useSubscriberAddMutation();

  const [removeSubscribers] = useSubscriberRemoveMutation();

  const onSelectedSubscribersChange = (newSubscribers: MemberSelect_UserInfoFragment[]) => {
    // If the new subscribers list is longer than the selected subscribers list, we need to add the new subscribers
    if (newSubscribers.length > selectedSubscribers.length) {
      // Filtering the new subscribers to find those that are not already in the selected subscribers list
      const additionalSubscribers: MemberSelect_UserInfoFragment[] = newSubscribers.filter(
        (newSubscriber: MemberSelect_UserInfoFragment) =>
          !selectedSubscribers.find(
            (selectedSubscriber: MemberSelect_UserInfoFragment) =>
              selectedSubscriber.employee?.id === newSubscriber.employee?.id
          )
      );

      addSubscribers({
        variables: {
          input: {
            sourceId,
            sourceType,
            employeeIds: additionalSubscribers.map(
              (subscriber: MemberSelect_UserInfoFragment) => subscriber.employee?.id
            ),
          },
        },
      }).catch((err) => {
        enqueueSnackbar(err.message, { variant: 'error' });
      });
      // If the new subscribers list is shorter than the selected subscribers list, we need to remove the subscribers
    } else {
      // Filtering the selected subscribers to find those that are not in the new subscribers list, thereby removing them
      const removedSubscribers: MemberSelect_UserInfoFragment[] = selectedSubscribers.filter(
        (selectedSubscriber: MemberSelect_UserInfoFragment) =>
          !newSubscribers.find(
            (newSubscriber: MemberSelect_UserInfoFragment) =>
              selectedSubscriber.employee?.id === newSubscriber.employee?.id
          )
      );

      removeSubscribers({
        variables: {
          input: {
            sourceId,
            sourceType,
            employeeIds: removedSubscribers.map((subscriber: MemberSelect_UserInfoFragment) => subscriber.employee?.id),
          },
        },
      }).catch((err) => {
        enqueueSnackbar(err.message, { variant: 'error' });
      });
    }

    // Updating the selected subscribers list with the new subscribers list
    setSelectedSubscribers(newSubscribers);
  };

  const isCurrentEmployeeSubscribed = selectedSubscribers.some(
    (subscriber) => subscriber.employee?.id === currentEmployeeId
  );

  const onSubscribeButtonClick = () => {
    if (isCurrentEmployeeSubscribed) {
      onSelectedSubscribersChange(
        selectedSubscribers.filter((subscriber) => subscriber.employee?.id !== currentEmployeeId)
      );
    } else {
      onSelectedSubscribersChange([
        ...selectedSubscribers,
        {
          ...EMPTY_MEMBER_SELECT_USER_INFO_FRAGMENT_OBJECT,
          userId: currentUserInfo?.userId,
          employee: {
            id: currentEmployeeId,
            internalProfilePictureUrl: currentEmployeeDetails?.internalProfilePictureUrl || '',
            fullName: currentEmployeeDetails?.fullName || '',
          },
        },
      ]);
    }
  };

  return (
    <Stack direction="row" alignItems="center" sx={sxProps.root}>
      {showSubscribeButton ? (
        <Button data-testid="subscribe-button" variant="text" onClick={onSubscribeButtonClick} size="small">
          {isCurrentEmployeeSubscribed
            ? t('hiring-dashboard-common:common.activity.unsubscribe')
            : t('hiring-dashboard-common:common.subscribe')}
        </Button>
      ) : null}
      <MultiMemberSelect
        selectedEmployeeIds={selectedSubscribers.map((subscriber) => subscriber.employee?.id)}
        placeholderText={t('hiring-dashboard-common:common.select.searchEmployees')}
        onChange={onSelectedSubscribersChange}
        getLabel={(values: MemberSelect_UserInfoFragment[]) => {
          if (values.length > 0) {
            return (
              <Button data-testid="filter-select-button" endIcon={<TriangleDownIcon />} variant="text">
                <EmployeeFacePile
                  sx={sxProps.employeeFacePile}
                  employees={selectedSubscribers.map((subscriber) => ({
                    id: subscriber.employee?.id,
                    fullName: subscriber.employee?.fullName || '',
                    internalProfilePictureUrl: subscriber.employee?.internalProfilePictureUrl || '',
                  }))}
                  max={3}
                />
              </Button>
            );
          }
          return (
            <IconButton data-testid="filter-select-button">
              <UserAddIcon />
            </IconButton>
          );
        }}
      />
    </Stack>
  );
};

export default SubscribersSelect;
